/**
 * TimeframeSlider component - Accessible slider for selecting future timeframe.
 *
 * Design: DENiZEN
 * - Soft borders with rounded corners
 * - Subtle shadows
 * - Rounded step indicators
 * - WCAG 2.1 AA compliant
 */

/* eslint-disable react-refresh/only-export-components -- Exports constants alongside component */

import { useState, useCallback } from 'react'
import * as Slider from '@radix-ui/react-slider'

/**
 * Timeframe options with display labels and values
 */
export const TIMEFRAME_OPTIONS = [
  { value: '6mo', label: '6 months', shortLabel: '6mo' },
  { value: '1yr', label: '1 year', shortLabel: '1yr' },
  { value: '3yrs', label: '3 years', shortLabel: '3yrs' },
  { value: '5yrs', label: '5 years', shortLabel: '5yrs' },
  { value: '10yrs', label: '10 years', shortLabel: '10yrs' },
  { value: '15yrs', label: '15 years', shortLabel: '15yrs' },
  { value: '30yrs', label: '30 years', shortLabel: '30yrs' },
  { value: '50yrs', label: '50 years', shortLabel: '50yrs' },
] as const

export type TimeframeValue = (typeof TIMEFRAME_OPTIONS)[number]['value']

export interface TimeframeSliderProps {
  /**
   * Callback when timeframe is submitted
   */
  onSubmit: (timeframe: TimeframeValue) => void
  /**
   * Default selected timeframe index (0-7)
   */
  defaultIndex?: number
  /**
   * Whether the slider is disabled
   */
  disabled?: boolean
  /**
   * Whether submission is in progress
   */
  isLoading?: boolean
  /**
   * Accessible label for the slider
   */
  ariaLabel?: string
  /**
   * Contextual prompt/question to display above the slider
   */
  prompt?: string
}

export function TimeframeSlider({
  onSubmit,
  defaultIndex = 4, // Default to 10 years
  disabled = false,
  isLoading = false,
  ariaLabel = 'Select how far into the future to envision',
  prompt,
}: TimeframeSliderProps) {
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex)

  const selectedOption = TIMEFRAME_OPTIONS[selectedIndex] ?? TIMEFRAME_OPTIONS[4]

  const handleValueChange = useCallback((values: number[]) => {
    const newIndex = values[0]
    if (newIndex !== undefined) {
      setSelectedIndex(newIndex)
    }
  }, [])

  const handleSubmit = useCallback(() => {
    if (!disabled && !isLoading) {
      onSubmit(selectedOption.value)
    }
  }, [disabled, isLoading, onSubmit, selectedOption.value])

  const isDisabled = disabled || isLoading

  return (
    <div className="w-full">
      {/* Contextual prompt/question - Magical reveal, appears first */}
      {prompt && (
        <div className="mb-6 text-center opacity-0 animate-reveal-magic">
          <p className="text-lg font-body text-dark-green leading-relaxed">
            {prompt}
          </p>
        </div>
      )}

      {/* Selected value display - Magical reveal with delay */}
      <div className="mb-6 text-center opacity-0 animate-reveal-magic-delayed-1">
        <div className="inline-block px-6 py-3 bg-light-green border border-dark-green/15 shadow-denizen rounded-full animate-glow-pulse">
          <span className="text-3xl font-display font-bold text-dark-green">
            {selectedOption.label}
          </span>
        </div>
        <p className="mt-3 text-sm font-body text-muted">
          into the future
        </p>
      </div>

      {/* Slider container - Magical reveal with more delay */}
      <div className="denizen-card p-6 opacity-0 animate-reveal-magic-delayed-2">
        {/* Slider - DENiZEN style */}
        <Slider.Root
          className="relative flex items-center select-none touch-none w-full h-8"
          value={[selectedIndex]}
          onValueChange={handleValueChange}
          max={TIMEFRAME_OPTIONS.length - 1}
          min={0}
          step={1}
          disabled={isDisabled}
          aria-label={ariaLabel}
        >
          <Slider.Track className="bg-white border border-dark-green/10 relative grow h-4 rounded-full">
            <Slider.Range className="absolute bg-mid-green h-full rounded-full" />
          </Slider.Track>
          <Slider.Thumb
            className={`
              block w-8 h-8 bg-teal border border-dark-green/15 shadow-denizen rounded-full
              focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green focus-visible:ring-offset-2
              transition-transform duration-150
              hover:-translate-y-1 hover:shadow-denizen
              ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-grab active:cursor-grabbing'}
            `}
            aria-label={`Selected: ${selectedOption.label}`}
          />
        </Slider.Root>

        {/* Step labels - DENiZEN chips */}
        <div className="flex justify-between mt-6">
          {TIMEFRAME_OPTIONS.map((option, index) => (
            <button
              key={option.value}
              type="button"
              onClick={() => !isDisabled && setSelectedIndex(index)}
              disabled={isDisabled}
              className={`
                text-xs font-display font-bold tracking-wider
                px-2 py-1 border border-dark-green/10
                rounded-full transition-all duration-150
                focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green
                ${index === selectedIndex
                  ? 'bg-light-green text-dark-green shadow-denizen-sm -translate-y-0.5'
                  : 'bg-white text-muted hover:bg-light-green hover:text-dark-green hover:-translate-y-0.5'
                }
                ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
              `}
              aria-label={`Select ${option.label}`}
              tabIndex={isDisabled ? -1 : 0}
            >
              {option.shortLabel}
            </button>
          ))}
        </div>
      </div>

      {/* Submit button - Magical reveal with final delay */}
      <div className="mt-6 opacity-0 animate-reveal-magic-delayed-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isDisabled}
          className={`
            w-full py-4 px-6 font-display font-bold text-sm tracking-wider
            border border-dark-green/15 rounded-full
            shadow-denizen transition-all duration-150
            focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green focus-visible:ring-offset-2
            ${isDisabled
              ? 'bg-muted text-white cursor-not-allowed'
              : 'bg-dark-green text-white hover:-translate-y-1 hover:shadow-denizen active:translate-y-0 active:shadow-denizen-sm'
            }
          `}
          aria-label={`Continue with ${selectedOption.label} timeframe`}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="w-5 h-5 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Submitting...
            </span>
          ) : (
            'Continue'
          )}
        </button>
      </div>

      {/* Keyboard hint - Appears with button */}
      <p className="mt-4 text-xs text-muted text-center font-body flex items-center justify-center gap-2 flex-wrap opacity-0 animate-reveal-magic-delayed-3">
        Use
        <kbd className="px-2 py-1 bg-white border border-dark-green/10 font-display font-bold text-[10px] shadow-denizen-sm rounded-full">←</kbd>
        <kbd className="px-2 py-1 bg-white border border-dark-green/10 font-display font-bold text-[10px] shadow-denizen-sm rounded-full">→</kbd>
        arrow keys to adjust
      </p>
    </div>
  )
}

export default TimeframeSlider
